import React from "react"
import i18next from "../i18n"

const RELOAD_GUARD_KEY = "tna:chunkload:reloaded"

const isChunkLoadError = (error) => {
  const name = String(error?.name || "")
  const message = String(error?.message || "")

  return (
    name === "ChunkLoadError" ||
    message.includes("ChunkLoadError") ||
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  )
}

class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { chunkError: null, nonChunkError: null }
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      this.setState({ chunkError: error })

      try {
        const hasReloaded = window?.sessionStorage?.getItem(RELOAD_GUARD_KEY) === "1"
        if (!hasReloaded) {
          window?.sessionStorage?.setItem(RELOAD_GUARD_KEY, "1")
          window?.location?.reload()
        }
      } catch (e) {
        // ignore
      }

      return
    }

    this.setState({ nonChunkError: error })
  }

  handleReload = () => {
    try {
      window?.sessionStorage?.removeItem(RELOAD_GUARD_KEY)
    } catch (e) {
      // ignore
    }
    window?.location?.reload()
  }

  render() {
    const { nonChunkError, chunkError } = this.state

    if (nonChunkError) {
      throw nonChunkError
    }

    if (chunkError) {
      return (
        <div className="min-h-[40vh] flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-sm">
            <div className="text-lg font-semibold">
              {i18next.t("chunk_load_failed_title", {
                ns: "errors",
                defaultValue: "Unable to load the page",
              })}
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {i18next.t("chunk_load_failed_description", {
                ns: "errors",
                defaultValue:
                  "The app may have been updated. Please reload the page and try again.",
              })}
            </div>
            <button
              type="button"
              onClick={this.handleReload}
              className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {i18next.t("reload_page", {
                ns: "errors",
                defaultValue: "Reload",
              })}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ChunkLoadErrorBoundary
